import React, { useRef, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { AVAILABILITY_AVAILABLE, errorMessage } from './budgetTags';
import BudgetTagToggle from './BudgetTagToggle';

import { idempotencyHeader } from 'Helpers/apiUtils';

import Alert from 'CommonComponents/Alert';
import CheckboxFormField from 'CommonComponents/CheckboxFormField';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextFormField from 'CommonComponents/TextFormField';

const TagCategoriesSchema = Yup.object().shape({
    categories: Yup.array(Yup.string()),
    budget_tags: Yup.array(Yup.string()),
    new_category: Yup.string().max(255),
});

export default function CreateTagCategoryModal(props) {
    const [error, setError] = useState(null);
    const formRef = useRef(null);

    // The modal stays mounted between openings, so unsaved edits must be discarded on close.
    const close = (handler) => () => {
        formRef.current?.resetForm();
        setError(null);
        handler();
    };

    return (
        <>
            <div id={props.modalId} ref={props.modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
                <div className="modal__container-large">
                    <div className="modal__inner-container">
                        <ModalHeader title={$t('Manage codesets')} close={close(props.onClose)}></ModalHeader>
                        <div className="tw-rounded-b">
                            <div className="modal__form-container">
                                <Formik
                                    innerRef={formRef}
                                    enableReinitialize
                                    initialValues={{
                                        categories: props.activeTagCategories?.map(({ id }) => String(id)) || [],
                                        budget_tags: props.tagCategories?.filter(({ is_budget_tag }) => is_budget_tag).map(({ id }) => String(id)) || [],
                                        new_category: '',
                                    }}
                                    validationSchema={TagCategoriesSchema}
                                    onSubmit={(values, actions) => {
                                        setError(null);
                                        axios
                                            .post(props.storeUrl, values, {
                                                headers: { ...idempotencyHeader() },
                                            })
                                            .then(() => {
                                                props.onSuccess();
                                                actions.resetForm();
                                            })
                                            .catch((e) => setError(errorMessage(e)))
                                            .finally(() => {
                                                actions.setSubmitting(false);
                                            });
                                    }}
                                >
                                    {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                                        <>
                                            <div className="tw-space-y-4">
                                                <div className="inbud-table_wrapper">
                                                    <table className="inbud-table">
                                                        <thead>
                                                            <tr>
                                                                <th>{$t('Show')}</th>
                                                                <th>{$t('Codeset')}</th>
                                                                <th>{$t('Items')}</th>
                                                                <th>{$t('Use as budget tag')}</th>
                                                                <th>{$t('Taggable')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {props.tagCategories?.map((category) => (
                                                                <CodesetRow
                                                                    key={category.id}
                                                                    category={category}
                                                                    budgetTags={values.budget_tags}
                                                                    canManageTags={props.canManageTags}
                                                                    onBudgetTagChange={(checked) =>
                                                                        setFieldValue(
                                                                            'budget_tags',
                                                                            checked
                                                                                ? [...values.budget_tags, String(category.id)]
                                                                                : values.budget_tags.filter((id) => id !== String(category.id)),
                                                                        )
                                                                    }
                                                                />
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {errors.categories && touched.categories && (
                                                    <div className="tw-text-red-500 tw-text-sm">{errors.categories}</div>
                                                )}
                                                <TextFormField
                                                    type="text"
                                                    name="new_category"
                                                    label={$t('Add a custom codeset')}
                                                    placeholder={$t('Add a custom codeset')}
                                                ></TextFormField>
                                                <div
                                                    role="status"
                                                    className="tw-rounded-e-lg tw-border-s-[3px] tw-border-blue tw-bg-blue-15 tw-px-4 tw-py-3 tw-text-sm tw-text-charcoal-75"
                                                >
                                                    {$t(
                                                        'Departments, Venues and Events cannot be used as budget tags: a properly built budget already carries its costs in each of them, so tagging would double count. Tags are a reporting view only and never move budget or change approvals.',
                                                    )}
                                                </div>
                                                {error && <Alert type="error">{error}</Alert>}
                                            </div>

                                            <ModalFooter
                                                cancelButtonText={$t('Cancel')}
                                                primaryButtonText={$t('Save')}
                                                submitButtonVariant="primary"
                                                onCancel={close(props.onCancel)}
                                                handleSubmit={handleSubmit}
                                                isSubmitting={isSubmitting}
                                            ></ModalFooter>
                                        </>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function CodesetRow({ category, budgetTags, canManageTags, onBudgetTagChange }) {
    const isAvailable = category.budget_tag_availability === AVAILABILITY_AVAILABLE;
    const isOn = isAvailable && budgetTags.includes(String(category.id));

    return (
        <tr>
            <td>
                <CheckboxFormField type="checkbox" name="categories" value={String(category.id)} aria-label={$t('Show {{name}}', { name: category.name })} />
            </td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <span className="tw-font-semibold tw-text-charcoal">{category.name}</span>
                    {category.is_custom && (
                        <span className="tw-shrink-0 tw-bg-blue-15 tw-text-blue tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium">
                            {$t('custom')}
                        </span>
                    )}
                </div>
                {!isAvailable && category.budget_tag_unavailable_reason && (
                    <div className="tw-text-xs tw-text-charcoal-50">{category.budget_tag_unavailable_reason}</div>
                )}
            </td>
            <td>{category.items_count ?? category.tags_count ?? 0}</td>
            <td>
                <BudgetTagToggle
                    aria-label={$t('Use {{name}} as budget tag', { name: category.name })}
                    checked={isOn}
                    unavailable={!isAvailable || !canManageTags}
                    onChange={(e) => onBudgetTagChange(e.target.checked)}
                />
            </td>
            <td>{isOn ? <TaggableProgress category={category} /> : <span className="tw-text-charcoal-50">—</span>}</td>
        </tr>
    );
}

// Switching a codeset on makes every item taggable, so an unsaved switch-on
// previews as all items taggable.
function TaggableProgress({ category }) {
    const total = category.tags_count ?? 0;
    const count = category.is_budget_tag ? (category.taggable_count ?? 0) : total;
    const percent = total ? Math.round((count / total) * 100) : 0;

    return (
        <div className="tw-flex tw-items-center tw-gap-x-2">
            <div className="tw-w-10 tw-h-1.5 tw-rounded-full tw-bg-charcoal-15 tw-overflow-hidden">
                <div className="tw-h-full tw-rounded-full tw-bg-green" style={{ width: `${percent}%` }} />
            </div>
            <span className="tw-text-xs tw-text-charcoal-75">{`${count}/${total}`}</span>
        </div>
    );
}
